import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function adminHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('hrv_admin_token')}` };
}

const STATUS_COLORS = {
  success: { bg: '#f0fff4', color: '#276749', border: '#9ae6b4' },
  failed:  { bg: '#fff5f5', color: '#c53030', border: '#fed7d7' },
  timeout: { bg: '#fffbeb', color: '#744210', border: '#fbd38d' },
};

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', flex: 1, minWidth: '160px' }}>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: color || '#1a202c' }}>{value ?? '—'}</div>
      <div style={{ color: '#4a5568', fontSize: '0.85rem', marginTop: '4px' }}>{label}</div>
      {sub && <div style={{ color: '#a0aec0', fontSize: '0.78rem', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

export default function AdminAIMonitor() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/ai/stats`, { headers: adminHeaders() });
      const d = await res.json();
      setStats(d);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, 30000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const s = stats;

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#1a202c', margin: 0 }}>AI & System Monitor</h1>
          <p style={{ color: '#718096', margin: '6px 0 0' }}>
            Auto-refreshes every 30s
            {lastRefresh && <span> · Last: {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <button onClick={fetchStats} style={{ padding: '9px 18px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem' }}>
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ color: '#718096', textAlign: 'center', padding: '60px' }}>Loading…</div>
      ) : (
        <>
          {/* Ollama status */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#2d3748' }}>Ollama Status</h3>
              <span style={{
                padding: '3px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700',
                background: s?.ollama_status === 'online' ? '#f0fff4' : '#fff5f5',
                color: s?.ollama_status === 'online' ? '#276749' : '#c53030',
                border: `1px solid ${s?.ollama_status === 'online' ? '#9ae6b4' : '#fed7d7'}`,
              }}>
                {s?.ollama_status === 'online' ? '● Online' : '● Offline'}
              </span>
            </div>
            {s?.models?.length > 0 ? (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {s.models.map(m => (
                  <div key={m.name} style={{ padding: '8px 14px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.875rem' }}>{m.name}</div>
                    <div style={{ color: '#718096', fontSize: '0.78rem' }}>{m.size}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#a0aec0', fontSize: '0.875rem' }}>
                {s?.ollama_status === 'online' ? 'No models loaded' : 'Cannot reach Ollama server'}
              </div>
            )}
          </div>

          {/* Stat cards */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <StatCard label="AI Calls Today"     value={s?.total_calls_today}   color="#6366f1" />
            <StatCard label="Failed (24h)"        value={s?.failed_calls_24h}    color={s?.failed_calls_24h > 0 ? '#c53030' : '#276749'} />
            <StatCard label="Avg Response"        value={s?.avg_duration_ms ? `${(s.avg_duration_ms/1000).toFixed(1)}s` : '—'} />
            <StatCard label="Success Rate (24h)"  value={s?.success_rate != null ? `${s.success_rate}%` : '—'}
              color={s?.success_rate >= 90 ? '#276749' : s?.success_rate >= 70 ? '#744210' : '#c53030'} />
          </div>

          {/* Recent calls */}
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '20px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#2d3748' }}>Recent AI Calls</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Time', 'Feature', 'Model', 'Duration', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!s?.recent_calls?.length ? (
                  <tr><td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: '#a0aec0' }}>No AI calls recorded yet</td></tr>
                ) : s.recent_calls.map((call, i) => {
                  const cs = STATUS_COLORS[call.status] || STATUS_COLORS.failed;
                  return (
                    <tr key={call.id} style={{ borderBottom: '1px solid #f0f4f8', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '10px 16px', color: '#718096', fontSize: '0.8rem' }}>
                        {call.created_at ? new Date(call.created_at).toLocaleTimeString() : '—'}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#2d3748', fontSize: '0.875rem', fontWeight: '500' }}>
                        {(call.endpoint || '').replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#4a5568', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {(call.model_used || '—').slice(0, 30)}
                      </td>
                      <td style={{ padding: '10px 16px', color: '#4a5568', fontSize: '0.875rem' }}>
                        {call.duration_ms != null ? `${(call.duration_ms / 1000).toFixed(1)}s` : '—'}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', background: cs.bg, color: cs.color, border: `1px solid ${cs.border}` }}>
                          {call.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Failed calls with errors */}
          {s?.failed_calls?.length > 0 && (
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#fff5f5' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#c53030' }}>Failed Calls — Last 24h</h3>
              </div>
              <div style={{ padding: '16px' }}>
                {s.failed_calls.map(call => (
                  <div key={call.id} style={{ padding: '12px 14px', background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontWeight: '600', color: '#c53030', fontSize: '0.875rem' }}>{(call.endpoint || '').replace(/_/g, ' ')}</span>
                      <span style={{ color: '#718096', fontSize: '0.78rem' }}>{call.created_at ? new Date(call.created_at).toLocaleString() : ''}</span>
                    </div>
                    <div style={{ color: '#4a5568', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                      {call.error_message || 'No error details'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
